import { useEffect, lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Admin Components (eagerly loaded — critical path)
import AdminLogin from '@/components/admin/AdminLogin';
import AdminLayout from '@/components/admin/AdminLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// Wrapper pages (new grouped navigation)
const SinyalSahamPage = lazy(() => import('@/pages/admin/SinyalSahamPage'));
const PenggunaPage = lazy(() => import('@/pages/admin/PenggunaPage'));
const KontenPage = lazy(() => import('@/pages/admin/KontenPage'));
const SupportPage = lazy(() => import('@/pages/admin/SupportPage'));
const SistemPage = lazy(() => import('@/pages/admin/SistemPage'));
const SistemSahamPage = lazy(() => import('@/pages/admin/SistemSahamPage'));

// Existing page components (now used as tab children)
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const SignalManagementPage = lazy(() => import('@/pages/admin/SignalManagementPage'));
const StockAnalysisPage = lazy(() => import('@/pages/admin/StockAnalysisPage'));
const StockCategoriesPage = lazy(() => import('@/pages/admin/StockCategoriesPage'));
const ThinkmindMonitorPage = lazy(() => import('@/pages/admin/ThinkmindMonitorPage'));
const SignalQAPage = lazy(() => import('@/pages/admin/SignalQAPage'));
const AICanaryPage = lazy(() => import('@/pages/admin/AICanaryPage'));
const PipelineMonitorPage = lazy(() => import('@/pages/admin/PipelineMonitorPage'));
const UserManagementPage = lazy(() => import('@/pages/admin/UserManagementPage'));
const UnifiedTierDashboard = lazy(() => import('@/components/admin/UnifiedTierDashboard'));
const AdminPaymentsPage = lazy(() => import('@/pages/admin/AdminPaymentsPage'));
const BlogGeneratorPage = lazy(() => import('@/pages/admin/BlogGeneratorPage'));
const MarketingAgentPage = lazy(() => import('@/pages/admin/MarketingAgentPage'));
const TPForwardLogPage = lazy(() => import('@/pages/admin/TPForwardLogPage'));
const NotificationManagementPage = lazy(() => import('@/pages/admin/NotificationManagementPage'));
const EmailAnalyticsPage = lazy(() => import('@/pages/admin/EmailAnalyticsPage'));
const SupportTicketsPage = lazy(() => import('@/pages/admin/SupportTicketsPage'));
const KnowledgeBaseManagerPage = lazy(() => import('@/pages/admin/KnowledgeBaseManagerPage'));
const AnalyticsInsightsPage = lazy(() => import('@/pages/admin/AnalyticsInsightsPage'));
const AIServicesMonitoringPage = lazy(() => import('@/pages/admin/AIServicesMonitoringPage'));
const FeatureFlagsPage = lazy(() => import('@/pages/admin/FeatureFlagsPage'));
const ActivityLogPage = lazy(() => import('@/pages/admin/ActivityLogPage'));
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'));
const WhatsAppUsersPage = lazy(() => import('@/pages/admin/WhatsAppUsersPage'));
const AdminReferralWithdrawalsPage = lazy(() => import('@/pages/admin/AdminReferralWithdrawalsPage'));
const AdminReferralStatsPage = lazy(() => import('@/pages/admin/AdminReferralStatsPage'));
const AdminWaitlistPage = lazy(() => import('@/pages/admin/AdminWaitlistPage'));

// Auth Hook
import { useAdminAuth } from '@/hooks/useAdminAuth';

// Global Styles
import '@/index.css';

function PageFallback() {
  return (
    <div className="flex items-center justify-center p-12">
      <LoadingSpinner size="lg" />
    </div>
  );
}

const AdminApp: React.FC = () => {
  const { isAuthenticated, isLoading, login, logout } = useAdminAuth();

  // Track theme for Toaster styling
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('admin_theme');
    return saved ? saved === 'dark' : true;
  });

  useEffect(() => {
    document.title = 'TraderTekno Admin';
    document.body.classList.add('admin-app');
    return () => {
      document.body.classList.remove('admin-app');
    };
  }, []);

  // Sync theme for Toaster
  useEffect(() => {
    const sync = () => {
      const saved = localStorage.getItem('admin_theme');
      setIsDarkMode(saved ? saved === 'dark' : true);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('theme-change', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('theme-change', sync);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="admin-app">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: isDarkMode
              ? { background: '#1e293b', color: '#f1f5f9', border: '1px solid #334155' }
              : { background: '#ffffff', color: '#1e293b', border: '1px solid #e2e8f0' },
          }}
        />

        <Routes>
          {/* Login Route */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <AdminLogin onLogin={login} />}
          />

          {/* Protected Admin Routes */}
          {isAuthenticated ? (
            <>
              <Route path="/" element={<AdminLayout onLogout={logout} />}>
                <Route index element={<Navigate to="/dashboard" replace />} />

                {/* Dashboard — standalone */}
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <AdminDashboard />
                    </Suspense>
                  }
                />

                {/* Sinyal & Saham — grouped */}
                <Route
                  path="sinyal"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SinyalSahamPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="manajemen" replace />} />
                  <Route path="manajemen" element={<SignalManagementPage />} />
                  <Route path="monitor" element={<PipelineMonitorPage />} />
                  <Route path="analisis" element={<StockAnalysisPage />} />
                  <Route path="thinkmind" element={<ThinkmindMonitorPage />} />
                  <Route path="qa" element={<SignalQAPage />} />
                  <Route path="ai-canary" element={<AICanaryPage />} />
                  <Route path="kategori" element={<StockCategoriesPage />} />
                </Route>

                {/* Pengguna — grouped */}
                <Route
                  path="pengguna"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <PenggunaPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="daftar" replace />} />
                  <Route path="daftar" element={<UserManagementPage />} />
                  <Route path="tier" element={<UnifiedTierDashboard />} />
                  <Route path="pembayaran" element={<AdminPaymentsPage />} />
                  <Route path="whatsapp" element={<WhatsAppUsersPage />} />
                  <Route path="pencairan-referral" element={<AdminReferralWithdrawalsPage />} />
                  <Route path="performa-referral" element={<AdminReferralStatsPage />} />
                  <Route path="waitlist" element={<AdminWaitlistPage />} />
                </Route>

                {/* Konten — grouped */}
                <Route
                  path="konten"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <KontenPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="blog" replace />} />
                  <Route path="blog" element={<BlogGeneratorPage />} />
                  <Route path="marketing-agent" element={<MarketingAgentPage />} />
                  <Route path="tp-forward-log" element={<TPForwardLogPage />} />
                  <Route path="notifikasi" element={<NotificationManagementPage />} />
                  <Route path="email" element={<EmailAnalyticsPage />} />
                </Route>

                {/* Support — grouped */}
                <Route
                  path="support"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SupportPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="tiket" replace />} />
                  <Route path="tiket" element={<SupportTicketsPage />} />
                  <Route path="knowledge-base" element={<KnowledgeBaseManagerPage />} />
                </Route>

                {/* Sistem — grouped */}
                <Route
                  path="sistem"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SistemPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="analytics" replace />} />
                  <Route path="analytics" element={<AnalyticsInsightsPage />} />
                  {/* Dipindah ke Arsip Saham 27 Sep 2026; alamat lama dialihkan. */}
                  <Route path="ai-services" element={<Navigate to="/sistem-saham/ai-services" replace />} />
                  <Route path="feature-flags" element={<Navigate to="/sistem-saham/feature-flags" replace />} />
                  <Route path="activity-log" element={<ActivityLogPage />} />
                </Route>

                {/* Arsip Saham: sistem produk saham */}
                <Route
                  path="sistem-saham"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <SistemSahamPage />
                    </Suspense>
                  }
                >
                  <Route index element={<Navigate to="ai-services" replace />} />
                  <Route path="ai-services" element={<AIServicesMonitoringPage />} />
                  <Route path="feature-flags" element={<FeatureFlagsPage />} />
                </Route>

                {/* Settings — standalone */}
                <Route
                  path="settings"
                  element={
                    <Suspense fallback={<PageFallback />}>
                      <AdminSettings />
                    </Suspense>
                  }
                />

                {/* Legacy URL redirects */}
                <Route path="signals" element={<Navigate to="/sinyal/manajemen" replace />} />
                <Route path="stock-analysis" element={<Navigate to="/sinyal/analisis" replace />} />
                <Route path="stock-categories" element={<Navigate to="/sinyal/kategori" replace />} />
                <Route path="users" element={<Navigate to="/pengguna/daftar" replace />} />
                <Route path="tiers" element={<Navigate to="/pengguna/tier" replace />} />
                <Route path="subscriptions" element={<Navigate to="/pengguna/tier" replace />} />
                <Route path="payments" element={<Navigate to="/pengguna/pembayaran" replace />} />
                <Route path="blog-generator" element={<Navigate to="/konten/blog" replace />} />
                <Route path="notifications" element={<Navigate to="/konten/notifikasi" replace />} />
                <Route path="email-analytics" element={<Navigate to="/konten/email" replace />} />
                <Route path="support-tickets" element={<Navigate to="/support/tiket" replace />} />
                <Route path="knowledge-base" element={<Navigate to="/support/knowledge-base" replace />} />
                <Route path="analytics" element={<Navigate to="/sistem/analytics" replace />} />
                <Route path="ai-services" element={<Navigate to="/sistem/ai-services" replace />} />
                <Route path="feature-flags" element={<Navigate to="/sistem/feature-flags" replace />} />
                <Route path="activity-log" element={<Navigate to="/sistem/activity-log" replace />} />
                <Route path="system" element={<Navigate to="/dashboard" replace />} />
                <Route path="gemini" element={<Navigate to="/sistem/ai-services" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </>
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
      </div>
    </BrowserRouter>
  );
};

export default AdminApp;
